export const OPS_DEFAULTS = { project: 'norum-os-platform', region: 'europe-west4', repo: 'NorumGroup/norum-os' };
/** The Cloud Run services deploy.sh deploys. */
export const OPS_SERVICES = ['norum-os-core-api', 'norum-os-work-api'];
/** The Cloud Run jobs deploy.sh deploys. */
export const OPS_JOBS = [
    'norum-os-heartbeat',
    'norum-os-migrate',
    'norum-os-flex-migrate',
    'norum-os-org-sync',
    'norum-os-provision-agents',
    'norum-os-ledger-sync',
    'norum-os-readiness',
    'norum-os-seed-real',
];
/** The jobs that only check or repeat the existing mirror clock: sensitive. */
export const OPS_CHECK_JOBS = ['norum-os-readiness', 'norum-os-ledger-sync'];
/** These jobs change accounts, the organisation or real data: critical. */
export const OPS_APPLY_JOBS = ['norum-os-org-sync', 'norum-os-provision-agents', 'norum-os-seed-real'];
/** The clocks provision.sh creates. */
export const OPS_SCHEDULERS = ['norum-os-heartbeat', 'norum-os-ledger-sync'];
/** The APIs provision.sh enables, and the only ones the twin may propose enabling. */
export const OPS_APIS = [
    'admin.googleapis.com',
    'aiplatform.googleapis.com',
    'apikeys.googleapis.com',
    'calendar-json.googleapis.com',
    'chat.googleapis.com',
    'cloudscheduler.googleapis.com',
    'drive.googleapis.com',
    'gmail.googleapis.com',
    'maps-backend.googleapis.com',
    'meet.googleapis.com',
    'people.googleapis.com',
    'places.googleapis.com',
    'pubsub.googleapis.com',
    'secretmanager.googleapis.com',
    'sqladmin.googleapis.com',
];
const hours = { kind: 'number', min: 1, max: 168, default: 24, words: 'how many hours back' };
const max = { kind: 'number', min: 1, max: 100, default: 20, words: 'at most this many' };
const ghState = { kind: 'choice', values: ['open', 'closed', 'all'], default: 'open', words: 'which ones' };
export const OPERATIONS = [
    // ---- Reads: Google Cloud --------------------------------------------------
    {
        id: 'gcp.logs',
        title: 'Errors and warnings in the logs',
        purpose: 'Reads the latest warnings or errors one service or job wrote, so a failure is seen in its own words.',
        kind: 'read', tier: 'routine', target: 'gcp', tool: 'tech.gcp_logs',
        params: {
            target: { kind: 'choice', values: [...OPS_SERVICES, ...OPS_JOBS], words: 'which service or job' },
            severity: { kind: 'choice', values: ['WARNING', 'ERROR', 'CRITICAL'], default: 'ERROR', words: 'at least this severe' },
            hours,
            max,
        },
        needs: 'roles/logging.viewer',
    },
    {
        id: 'gcp.services',
        title: 'The services and what they run',
        purpose: 'Lists the Cloud Run services, the revision each serves and whether it is ready.',
        kind: 'read', tier: 'routine', target: 'gcp', tool: 'tech.gcp_run_services', params: {}, needs: 'roles/run.viewer',
    },
    {
        id: 'gcp.jobs',
        title: 'The jobs and their last run',
        purpose: 'Lists the Cloud Run jobs and how each one last ended.',
        kind: 'read', tier: 'routine', target: 'gcp', tool: 'tech.gcp_run_jobs', params: {}, needs: 'roles/run.viewer',
    },
    {
        id: 'gcp.executions',
        title: 'One job’s recent runs',
        purpose: 'Lists one job’s recent runs, when each started and how it ended.',
        kind: 'read', tier: 'routine', target: 'gcp', tool: 'tech.gcp_run_executions',
        params: { job: { kind: 'choice', values: OPS_JOBS, words: 'which job' } },
        needs: 'roles/run.viewer',
    },
    {
        id: 'gcp.apis',
        title: 'The APIs switched on',
        purpose: 'Lists the Google APIs enabled in the project, so a missing one is named.',
        kind: 'read', tier: 'routine', target: 'gcp', tool: 'tech.gcp_enabled_apis', params: {}, needs: 'roles/serviceusage.serviceUsageViewer',
    },
    {
        id: 'gcp.iam',
        title: 'Who holds which role',
        purpose: 'Reads the project’s IAM policy: which accounts hold which roles. It changes nothing.',
        kind: 'read', tier: 'routine', target: 'gcp', tool: 'tech.gcp_iam_policy', params: {}, needs: 'roles/iam.securityReviewer',
    },
    {
        id: 'gcp.secrets',
        title: 'The secrets and their age',
        purpose: 'Lists the secrets by name and when each was made. Never a secret’s value.',
        kind: 'read', tier: 'routine', target: 'gcp', tool: 'tech.gcp_secrets', params: {}, needs: 'roles/secretmanager.viewer',
    },
    {
        id: 'gcp.schedulers',
        title: 'The clocks',
        purpose: 'Lists the scheduler jobs, their schedule, whether each is paused and how it last ran.',
        kind: 'read', tier: 'routine', target: 'gcp', tool: 'tech.gcp_scheduler_jobs', params: {}, needs: 'roles/cloudscheduler.viewer',
    },
    {
        id: 'gcp.sql',
        title: 'The database instance',
        purpose: 'Reads the Cloud SQL instance’s state, tier and maintenance window.',
        kind: 'read', tier: 'routine', target: 'gcp', tool: 'tech.gcp_sql_instances', params: {}, needs: 'roles/cloudsql.viewer',
    },
    // ---- Reads: GitHub --------------------------------------------------------
    {
        id: 'github.commits',
        title: 'What landed on main',
        purpose: 'Lists the latest commits on main, who made each and what it says.',
        kind: 'read', tier: 'routine', target: 'github', tool: 'tech.github_commits', params: { max }, needs: 'contents: read',
    },
    {
        id: 'github.pulls',
        title: 'Pull requests',
        purpose: 'Lists the repository’s pull requests and where each stands.',
        kind: 'read', tier: 'routine', target: 'github', tool: 'tech.github_pulls', params: { state: ghState, max }, needs: 'pull_requests: read',
    },
    {
        id: 'github.issues',
        title: 'Issues',
        purpose: 'Lists the repository’s issues, the suggestions and briefs among them.',
        kind: 'read', tier: 'routine', target: 'github', tool: 'tech.github_issues', params: { state: ghState, max }, needs: 'issues: read',
    },
    {
        id: 'github.runs',
        title: 'CI runs on main',
        purpose: 'Lists the latest workflow runs on main and how each ended.',
        kind: 'read', tier: 'routine', target: 'github', tool: 'tech.github_runs', params: { max }, needs: 'actions: read',
    },
    // ---- Writes: the superadmin's (§17 as amended) ------------------------------
    {
        id: 'job.check',
        title: 'Run a checking job',
        purpose: 'Starts Readiness or the existing ledger mirror clock. Neither creates accounts or applies a roster.',
        kind: 'write', tier: 'sensitive', target: 'gcp',
        params: { job: { kind: 'choice', values: OPS_CHECK_JOBS, words: 'which job' } },
        needs: 'roles/run.jobsExecutorWithOverrides', long: true,
    },
    {
        id: 'job.apply',
        title: 'Run a job that changes things',
        purpose: 'Starts org-sync, agent account provisioning, or the real-data load under a token bound to this operation.',
        kind: 'write', tier: 'critical', target: 'gcp',
        params: { job: { kind: 'choice', values: OPS_APPLY_JOBS, words: 'which job' } },
        needs: 'roles/run.jobsExecutorWithOverrides', long: true,
    },
    {
        id: 'api.enable',
        title: 'Switch on an API',
        purpose: 'Enables one Google API the platform is built to use.',
        kind: 'write', tier: 'critical', target: 'gcp',
        params: { api: { kind: 'choice', values: OPS_APIS, words: 'which API' } },
        needs: 'roles/serviceusage.serviceUsageAdmin',
    },
    {
        id: 'scheduler.pause',
        title: 'Pause a clock',
        purpose: 'Pauses one scheduler job, so what it starts stops starting until it is resumed.',
        kind: 'write', tier: 'critical', target: 'gcp',
        params: { scheduler: { kind: 'choice', values: OPS_SCHEDULERS, words: 'which clock' } },
        needs: 'roles/cloudscheduler.admin',
    },
    {
        id: 'scheduler.resume',
        title: 'Resume a clock',
        purpose: 'Resumes one paused scheduler job.',
        kind: 'write', tier: 'critical', target: 'gcp',
        params: { scheduler: { kind: 'choice', values: OPS_SCHEDULERS, words: 'which clock' } },
        needs: 'roles/cloudscheduler.admin',
    },
];
export const OPERATION_IDS = new Set(OPERATIONS.map((o) => o.id));
export function operationById(id) {
    return OPERATIONS.find((o) => o.id === id);
}
/** The manifests the standing assignment is granted: one per read. */
export const KEEPER_READ_TOOLS = OPERATIONS.filter((o) => o.kind === 'read').map((o) => o.tool);
/** Every IAM role the operations identity holds: exactly the catalogue's, which provision.sh grants (a test holds them equal). */
export const OPERATION_ROLES = [...new Set(OPERATIONS.filter((o) => o.target === 'gcp').map((o) => o.needs))].sort();
/**
 * The roles the twin's reads need, and nothing a write needs: what the
 * reader identity (norum-os-ops-reader) holds. Cloud Run, Scheduler, Secret
 * Manager and Cloud SQL accept only the full cloud-platform scope, refusing
 * the read-only one, so a read is minted with that scope as an identity whose
 * roles are viewers' alone: IAM, not the scope, keeps it a read.
 */
export const OPERATION_READ_ROLES = [...new Set(OPERATIONS.filter((o) => o.target === 'gcp' && o.kind === 'read').map((o) => o.needs))].sort();
/** What the GitHub App needs to read, beyond the Contents write it already holds. */
export const OPERATION_GITHUB_PERMISSIONS = [...new Set(OPERATIONS.filter((o) => o.target === 'github').map((o) => o.needs))].sort();
/** The longest the standing assignment lasts (§20). */
export const STANDING_MAX_DAYS = 30;
/** Five approvals in a row earn the sixth run of a sensitive write (§17 as amended). */
export const OPERATION_EARNED_AFTER = 5;
export class OperationParamsError extends Error {
}
/**
 * A caller's values, held to the catalogue. Unknown names are refused, not
 * dropped: a caller that sent one meant something by it, and a silent drop
 * would run a different operation from the one it asked for. A missing
 * value takes its default; a choice without one must be given.
 */
export function holdOperationParams(op, raw) {
    const given = raw === undefined || raw === null ? {} : raw;
    if (typeof given !== 'object' || Array.isArray(given))
        throw new OperationParamsError(`${op.id} takes its values as an object.`);
    const entries = Object.entries(given);
    const unknown = entries.map(([k]) => k).filter((k) => !(k in op.params));
    if (unknown.length > 0) {
        throw new OperationParamsError(`${op.id} takes [${Object.keys(op.params).join(', ') || 'nothing'}], not [${unknown.join(', ')}].`);
    }
    const out = {};
    for (const [name, spec] of Object.entries(op.params)) {
        const value = given[name];
        if (spec.kind === 'choice') {
            const chosen = value === undefined ? spec.default : value;
            if (typeof chosen !== 'string' || !spec.values.includes(chosen)) {
                throw new OperationParamsError(`${op.id}: ${name} is one of [${spec.values.join(', ')}].`);
            }
            out[name] = chosen;
        }
        else {
            const n = value === undefined ? spec.default : typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
            if (typeof n !== 'number' || !Number.isInteger(n) || n < spec.min || n > spec.max) {
                throw new OperationParamsError(`${op.id}: ${name} is a whole number from ${spec.min} to ${spec.max}.`);
            }
            out[name] = n;
        }
    }
    return out;
}
/** The resource a log entry belongs to, built from the catalogue's own names and nothing a caller wrote. */
export function opsResourceName(target) {
    if (OPS_SERVICES.includes(target)) {
        return `resource.type="cloud_run_revision" AND resource.labels.service_name="${target}"`;
    }
    if (OPS_JOBS.includes(target)) {
        return `resource.type="cloud_run_job" AND resource.labels.job_name="${target}"`;
    }
    throw new OperationParamsError(`${target} is not a service or job the platform deploys.`);
}
/**
 * Build a read's call from held values. The log filter is the one body a
 * read carries, and it is composed only from `opsResourceName`, a severity
 * from the enumeration and a time the platform computes.
 */
export function readCall(op, params, config, now) {
    if (op.kind !== 'read' || !op.tool)
        throw new OperationParamsError(`${op.id} is not a read.`);
    const base = { toolId: op.tool, args: {}, boundParams: {}, query: {} };
    switch (op.id) {
        case 'gcp.logs': {
            const since = new Date(now.getTime() - Number(params['hours']) * 3_600_000).toISOString();
            return {
                ...base,
                args: {
                    resourceNames: [`projects/${config.project}`],
                    filter: `${opsResourceName(String(params['target']))} AND severity>=${String(params['severity'])} AND timestamp>="${since}"`,
                    orderBy: 'timestamp desc',
                    pageSize: Number(params['max']),
                },
            };
        }
        case 'gcp.executions':
            return { ...base, boundParams: { job: String(params['job']) }, query: { pageSize: '10' } };
        case 'gcp.apis':
            return { ...base, query: { filter: 'state:ENABLED', pageSize: '200' } };
        case 'gcp.iam':
            return { ...base, args: { options: { requestedPolicyVersion: 3 } } };
        case 'github.commits':
        case 'github.runs':
            return { ...base, query: { [op.id === 'github.commits' ? 'sha' : 'branch']: 'main', per_page: String(params['max']) } };
        case 'github.pulls':
        case 'github.issues':
            return { ...base, query: { state: String(params['state']), per_page: String(params['max']) } };
        default:
            return base;
    }
}
export function writeCall(op, params, config, by) {
    const { project, region } = config;
    const run = `https://run.googleapis.com/v2/projects/${project}/locations/${region}/jobs`;
    const scheduler = `https://cloudscheduler.googleapis.com/v1/projects/${project}/locations/${region}/jobs`;
    switch (op.id) {
        case 'job.check':
            return { method: 'POST', url: `${run}/${String(params['job'])}:run`, body: {} };
        case 'job.apply': {
            const job = String(params['job']);
            // The real-data load is recorded as a named person (ADR-0138): the
            // superadmin performing it, from their session, never a model's choice.
            const body = job === 'norum-os-seed-real'
                ? { overrides: { containerOverrides: [{ args: ['packages/seed/dist/cli/seed-real.js', '--by', by.email, '--apply'] }] } }
                : {};
            return { method: 'POST', url: `${run}/${job}:run`, body };
        }
        case 'api.enable':
            return { method: 'POST', url: `https://serviceusage.googleapis.com/v1/projects/${project}/services/${String(params['api'])}:enable`, body: {} };
        case 'scheduler.pause':
            return { method: 'POST', url: `${scheduler}/${String(params['scheduler'])}:pause`, body: {} };
        case 'scheduler.resume':
            return { method: 'POST', url: `${scheduler}/${String(params['scheduler'])}:resume`, body: {} };
        default:
            throw new OperationParamsError(`${op.id} is not a write.`);
    }
}
/** The origin every write may reach. core-api refuses a URL outside it before any token is minted. */
export const WRITE_ORIGINS = ['https://run.googleapis.com', 'https://serviceusage.googleapis.com', 'https://cloudscheduler.googleapis.com'];
function surfaceOf(op, c) {
    const loc = `/projects/${c.project}/locations/${c.region}`;
    const gh = `/repos/${c.repo}`;
    switch (op.id) {
        case 'gcp.logs': return { origin: 'https://logging.googleapis.com', method: 'POST', path: '/v2/entries:list', maxBodyBytes: 4096 };
        case 'gcp.services': return { origin: 'https://run.googleapis.com', method: 'GET', path: `/v2${loc}/services` };
        case 'gcp.jobs': return { origin: 'https://run.googleapis.com', method: 'GET', path: `/v2${loc}/jobs` };
        case 'gcp.executions': return { origin: 'https://run.googleapis.com', method: 'GET', path: `/v2${loc}/jobs/{job}/executions`, query: ['pageSize'] };
        case 'gcp.apis': return { origin: 'https://serviceusage.googleapis.com', method: 'GET', path: `/v1/projects/${c.project}/services`, query: ['filter', 'pageSize'] };
        case 'gcp.iam': return { origin: 'https://cloudresourcemanager.googleapis.com', method: 'POST', path: `/v3/projects/${c.project}:getIamPolicy`, maxBodyBytes: 256 };
        case 'gcp.secrets': return { origin: 'https://secretmanager.googleapis.com', method: 'GET', path: `/v1/projects/${c.project}/secrets` };
        case 'gcp.schedulers': return { origin: 'https://cloudscheduler.googleapis.com', method: 'GET', path: `/v1${loc}/jobs` };
        case 'gcp.sql': return { origin: 'https://sqladmin.googleapis.com', method: 'GET', path: `/v1/projects/${c.project}/instances` };
        case 'github.commits': return { origin: 'https://api.github.com', method: 'GET', path: `${gh}/commits`, query: ['sha', 'per_page'] };
        case 'github.pulls': return { origin: 'https://api.github.com', method: 'GET', path: `${gh}/pulls`, query: ['state', 'per_page'] };
        case 'github.issues': return { origin: 'https://api.github.com', method: 'GET', path: `${gh}/issues`, query: ['state', 'per_page'] };
        case 'github.runs': return { origin: 'https://api.github.com', method: 'GET', path: `${gh}/actions/runs`, query: ['branch', 'per_page'] };
        default: throw new OperationParamsError(`${op.id} has no read surface.`);
    }
}
/** The Google Cloud read credential: the operations identity, read-only scope. The GitHub one: the ledger App's installation token. */
export const OPS_SECRET = { gcp: 'google.cloud_read', github: 'github.ledger_app' };
/**
 * The read manifests, as drafts for a superadmin to sign. Pure: the same
 * config and date give the same files, so a test holds the generator and
 * the ledger equal once they are written.
 */
export function operationManifests(config, issuedAt) {
    const expires = new Date(issuedAt);
    expires.setUTCFullYear(expires.getUTCFullYear() + 1);
    return OPERATIONS.filter((o) => o.kind === 'read').map((op) => {
        const s = surfaceOf(op, config);
        const host = new URL(s.origin).hostname;
        return {
            id: op.tool,
            version: 1,
            description: `${op.title}: a read of the platform's own ${op.target === 'gcp' ? `Google Cloud project ${config.project}` : `repository ${config.repo}`}, ` +
                `for a superadmin's twin under its standing assignment (Amendment 0013, ADR-0172). Fixed destination; it changes nothing.`,
            departments: ['tech'],
            requiredScopes: ['tech.operate'],
            surface: {
                transport: 'http',
                origin: s.origin,
                methods: [s.method],
                paths: [s.path],
                ...(s.query ? { query: s.query } : {}),
                ...(op.target === 'github' ? { fixedHeaders: { accept: 'application/vnd.github+json', 'x-github-api-version': '2022-11-28' } } : {}),
                maxBodyBytes: s.maxBodyBytes ?? 64,
            },
            egress: [host],
            rateLimit: { requestsPerMinute: 30, burst: 10, requestsPerDay: 2000 },
            secrets: [op.target === 'gcp' ? OPS_SECRET.gcp : OPS_SECRET.github],
            tier: 'routine',
            dualControl: false,
            hitlTriggers: [],
            issuedAt: issuedAt.toISOString(),
            expiresAt: expires.toISOString(),
        };
    });
}
//# sourceMappingURL=operations.js.map